// src/constants/tosUpdate.js
// ============================================================================
// TERMS VERSION + "WHAT'S NEW" COPY
//
// Single source for the returning-member update prompt. Bump CURRENT_TOS_VERSION
// here AND in functions/modules/auth.js (CURRENT_TOS_VERSION) whenever the terms
// text (TosAgreementScreen.js + web/terms-of-service.html) materially changes.
// A member is prompted when their stored version is BEHIND this one — compared
// numerically as major.minor by isTosVersionBehind below (auth.js uses the same
// parser), never by string inequality.
//
// Copy rules for this file (founder, 2026-08-03): lead with what the member
// GAINS, keep the terms summary to plain consequences, and never expose
// internal mechanics (thresholds, costs, roadmap). Every line here is
// founder-approved before deploy. TOS_UPDATE is Part C of TOS_V1.7_DRAFT.md
// (the whole object as built: v17 team memberships, plus v16 from
// STEP3_5A_TIERS_DESIGN.md 5.6 and v15 from TOS_V1.5_DRAFT.md with their
// v1.6 updates; TOS_V1.6_DRAFT.md is the 09-29 team draft, not the live v1.6),
// verbatim. Keep the lists SHORT — a wall of bullets gets dismissed unread.
// Highlight titles and terms points must stay unique (both lists render keyed
// by their text).
// ============================================================================

export const CURRENT_TOS_VERSION = '1.7';

export const TOS_UPDATE = {
  version: CURRENT_TOS_VERSION, // was a second hard-coded literal
  heading: "What's new on CritCast",
  intro: 'A few things have changed since you were last here.',

  // v1.7 (team memberships; Part C of TOS_V1.7_DRAFT.md): shown to every
  // member who is prompted. The modal is CUMULATIVE, four tiers
  // (tosUpdateListsFor): the v1.6 lists below are added when the stored
  // version is below 1.6, the v1.5 lists when it is below 1.5, and the v1.4
  // lists when it is below 1.4; a missing or unparseable version shows all
  // four. A member on 1.6 sees only these team lists.
  v17: {
    highlights: [
      {
        icon: 'people-outline',
        title: 'Team memberships',
        body: "Where a team offers one, you can join a team's membership for a monthly price and get the perks its captain chooses, like a team badge and members-only squad chat. It renews every month until you cancel in Your memberships.",
      },
    ],
    termsPoints: [
      "How a team membership payment is shared among a team's members is set out in Section 8 of the Terms; the person who paid is never included.",
      'If a team ends its team memberships, asks to disband, or falls below two people on its roster, your membership stops renewing and ends at the end of the month you have paid for. If the team is saved before it disbands, we tell you, and you can keep your membership from Your memberships.',
      "The team's captain can end your membership to the team; it then stops renewing and ends at the end of the month you paid for.",
      "You cannot join a team you are on, and the team's captain is told when you join and can see that you are a member.",
      "If your account isn't verified, you need a confirmed email address to join a team membership.",
    ],
  },

  // v1.6 (membership tiers and purchase limits; unchanged since its release):
  // also shown when the stored version is below 1.6, missing or unparseable
  // (see showsV16Lists).
  v16: {
    highlights: [
      {
        icon: 'layers-outline',
        title: 'Membership tiers',
        body: 'Where a creator offers channel memberships, they can offer up to four tiers: Spark, Amped, Overdrive and Maxed. Each tier includes the perks of the tiers below it, and you can switch to another tier the creator offers in Your memberships.',
      },
      {
        icon: 'shield-checkmark-outline',
        title: 'Purchase limits',
        body: 'Verified accounts no longer have a daily spending limit. Unverified accounts keep a limit of $500 in any 24 hours, now counted for each card too.',
      },
    ],
    termsPoints: [
      'You can switch to another tier the creator offers in Your memberships. Your new tier and its price start at your next monthly renewal, and nothing is charged when you switch.',
      'If a creator removes a perk, or makes it start at a tier above yours, you keep it until your first renewal that is at least 7 days after the change.',
      'A creator chooses which tiers can watch each members-only video and can change it at any time.',
      'The creator can see your tier, and your member badge in their chat shows it.',
      "If your account isn't verified, Super Crits, Rumbles and the first payment of a new membership together are limited to $500 in any 24 hours, counted for your account and for each card. Switching to a higher tier counts the difference in monthly price; renewals never count. Verifying your account removes the limit, and verified accounts have no daily spending limit.",
      "If a payment made with a card is disputed with the card issuer, that card may be stopped from being used for Super Crits, Rumbles and new memberships. A purchase stopped this way is not completed and you are not charged; a membership's first payment is not taken, or is refunded in full if it was.",
      'If a card issuer reports a payment as fraudulent, we may refund it. When a payment is reported or disputed as fraud, we may also place a hold on the account that made it, stop that card for Super Crits, Rumbles and new memberships, end right away any membership that payment or that account paid for with that card, and stop any other membership paid with that card from renewing.',
      "If your account isn't verified, you need a confirmed email address to join a channel membership.",
      "For creators: if one viewer's Super Crits and Rumbles, or those paid with one card, add up to an unusually large amount within 24 hours, your share of the purchase that reaches it and of later ones may stay pending for up to 30 days while we review them.",
    ],
  },

  // v1.5 (Part C of TOS_V1.5_DRAFT.md; its first point updated for tiers in
  // v1.6): also shown when the stored version is below 1.5, missing or
  // unparseable (see showsV15Lists).
  v15: {
    highlights: [
      {
        icon: 'star-outline',
        title: 'Channel memberships',
        body: "Where a creator offers one, you can join their channel for a monthly price and get the perks they choose. It renews every month until you cancel in Your memberships, and CritCash credits the creator a share when they can receive it.",
      },
    ],
    termsPoints: [
      'Memberships renew monthly at your price for your tier until you cancel in the app, and you keep your perks until the end of the month you paid for.',
      'If a creator ends their channel memberships or can no longer receive them, your membership ends at the end of a month you have paid for.',
      'The creator can see that you are a member, since when, and whether your membership is set to end.',
      'A creator can end your membership to their channel; it then stops renewing and ends at the end of the month you paid for.',
      'A chargeback on a membership payment ends that membership right away.',
    ],
  },

  // v1.4: also shown when the stored version is below 1.4, missing or unparseable.
  highlights: [
    {
      icon: 'trophy-outline',
      title: 'Events are free to enter',
      body: 'Joining events and leagues costs nothing. Crits are retired, so there is nothing to top up before you play.',
    },
    {
      icon: 'flash-outline',
      title: 'Super Crits and Rumbles support verified creators',
      body: 'When you send a Super Crit (a paid highlighted message on a live stream) or a Rumble (a boost for a live stream, a squad or an event), CritCash credits a share of it as compensation to the verified creators or players it supports. On an event, that happens once the results are final.',
    },
    {
      icon: 'shield-checkmark-outline',
      title: 'Creators keep what they are credited',
      body: 'If a Super Crit or a Rumble is later refunded or disputed, that alone never reduces what was already credited. Fraud and self-dealing are handled under our Fair Play rules.',
    },
    {
      icon: 'videocam-outline',
      title: 'Free accounts keep recordings',
      body: 'Your stream recordings are now kept on a free account too, for a set number of days. Some paid plans keep them longer.',
    },
  ],

  termsHeading: 'We also updated our Terms',
  termsIntro: 'The short version:',
  termsPoints: [
    'Crits are retired. Any left on your account have no cash value and cannot be bought, redeemed, or transferred.',
    'Super Crits and Rumbles are purchases from CritCash. They can only go to verified creators and players, never to your own channel or squad or to an event you are entered in. If an event you Rumbled is cancelled, is not completed, or has no one who can receive it, your Rumble is refunded.',
    'When you pay for a plan, a membership, a Super Crit, or a Rumble, your payment method is saved with our payment processor, and later purchases you confirm with one tap are charged to it. A chargeback can put the paying account on hold while we review it.',
    'Verified creators may earn compensation from CritCash. To receive it, you need to verify your ID and age.',
    "Stream recordings are kept for your plan's set number of days, then removed. If a paid plan ends, we will let you know and keep your older recordings for 14 days so you can download them. Videos you upload on a free account, or after a paid plan ends, may be removed after a period we set; we will let you know first.",
    'Plan perks outside free event entry can change. If we discontinue a major perk of a plan you paid for up front, we will offer a credit or refund for the time left on that plan.',
  ],

  readFullLabel: 'Read the full Terms',
  acceptLabel: 'Got it, continue',
  // Load-bearing, verbatim from 1.3.
  acceptFinePrint: 'Continuing means you accept the updated Terms of Service.',
};

// Shown on the Terms screen next to the version ("Version 1.7 · Effective
// <date>"), e.g. 'October 20, 2026'. Empty = no date rendered. Filled at
// release; scripts/check-tos-release.js blocks prod builds while it is empty.
export const TOS_EFFECTIVE_DATE = 'October 23, 2026';

// Shown briefly in the update prompt when saving the acceptance fails on OUR
// side (function down, timeout, network). The prompt then steps aside for the
// rest of the session: a platform failure must never gate the app.
export const TOS_ACCEPT_UNAVAILABLE =
  "We couldn't save that right now. You can keep going, and we'll ask again next time you open CritCast.";

// ---------------------------------------------------------------------------
// Version compare. IDENTICAL logic lives in functions/modules/auth.js — change
// both together. Versions are 'major.minor' strings; anything else is
// unparseable.
// ---------------------------------------------------------------------------
const TOS_VERSION_RE = /^(\d{1,4})\.(\d{1,4})$/;

// '1.4' -> [1, 4]; anything else (numbers, '1.4.1', ' 1.4', 'x') -> null.
export function parseTosVersion(v) {
  const m = typeof v === 'string' ? TOS_VERSION_RE.exec(v) : null;
  return m ? [Number(m[1]), Number(m[2])] : null;
}

// Parsed tuples in; < 0 when a is older, 0 when equal, > 0 when newer.
export function compareTosVersions(a, b) {
  return a[0] - b[0] || a[1] - b[1];
}

// True when a stored acceptance is older than CURRENT_TOS_VERSION ('1.10' is
// newer than '1.4'). A stored value that cannot be parsed counts as behind. A
// missing one (null / undefined / '') is NOT behind: the App gate never prompts
// an account with no stored version. A stored version NEWER than this bundle's
// is not behind either, so a stale bundle never re-prompts a member who is ahead.
export function isTosVersionBehind(v) {
  if (v == null || v === '') return false;
  const parsed = parseTosVersion(v);
  return !parsed || compareTosVersions(parsed, parseTosVersion(CURRENT_TOS_VERSION)) < 0;
}

// The update modal is cumulative, four tiers (v1.7): a member whose stored
// acceptance is below 1.6, missing or unparseable never saw the v1.6 lists,
// so they are shown under the v1.7 ones; below 1.5 (or missing or
// unparseable), the v1.5 lists too; below 1.4, the v1.4 lists too. At 1.6 or
// newer, the v1.7 lists alone.
export function showsV16Lists(acceptedVersion) {
  const parsed = parseTosVersion(acceptedVersion);
  return !parsed || compareTosVersions(parsed, [1, 6]) < 0;
}

export function showsV15Lists(acceptedVersion) {
  const parsed = parseTosVersion(acceptedVersion);
  return !parsed || compareTosVersions(parsed, [1, 5]) < 0;
}

export function showsV14Lists(acceptedVersion) {
  const parsed = parseTosVersion(acceptedVersion);
  return !parsed || compareTosVersions(parsed, [1, 4]) < 0;
}

// The lists the modal renders for a stored version: v1.7 always, then v1.6
// when showsV16Lists, then v1.5 when showsV15Lists, then v1.4 when
// showsV14Lists. Titles and points are unique across all four.
export function tosUpdateListsFor(acceptedVersion) {
  const withV16 = showsV16Lists(acceptedVersion);
  const withV15 = showsV15Lists(acceptedVersion);
  const withV14 = showsV14Lists(acceptedVersion);
  return {
    highlights: [
      ...TOS_UPDATE.v17.highlights,
      ...(withV16 ? TOS_UPDATE.v16.highlights : []),
      ...(withV15 ? TOS_UPDATE.v15.highlights : []),
      ...(withV14 ? TOS_UPDATE.highlights : []),
    ],
    termsPoints: [
      ...TOS_UPDATE.v17.termsPoints,
      ...(withV16 ? TOS_UPDATE.v16.termsPoints : []),
      ...(withV15 ? TOS_UPDATE.v15.termsPoints : []),
      ...(withV14 ? TOS_UPDATE.termsPoints : []),
    ],
  };
}

// The modal's quiet manage link (design 13.8), so no cancel ever waits on
// accepting new Terms. Both routes are exempt from the prompt in App.js.
// everLaunched comes from useMembershipsLive; since v1.7 (3.5b) the modal
// passes anyEverLaunched (channel or team memberships ever launched, 3.5b
// 13.12).
export function manageBillingLinkFor(everLaunched) {
  return everLaunched
    ? { label: 'Manage your plan or memberships', route: 'Memberships' }
    : { label: 'Manage your plan', route: 'Subscription' };
}
